import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef } from 'react';
import { NAVIGATION_FROM_APP, NAVIGATION_FROM_APP_CODE } from '@/constants/common';
import { ACTION_TAG, CROP_ACTION } from '@/constants/crop';
import { CropAction } from '@/models/cropGuide';
import { TechnicalContent } from '@/models/cropProblems';
import { getCookieValue, isArray } from '@/utils/helper';
import getCropActionDate from '@/utils/helpers/getCropActionDate';
import Caption from '../Caption';
import CaretDownIcon from '../icons/CaretDownIcon';
import RightIcon from '../icons/RightIcon';
import SubHeading from '../SubHeading';
import ProductCard from './ProductCard';
interface Props {
  cropAction: CropAction;
  onClick: VoidFunction;
  isCurrent: boolean;
  cropId: number;
  sowingDate: string | null;
}
const CropActionCard = ({
  cropAction,
  onClick,
  isCurrent,
  cropId,
  sowingDate
}: Props) => {
  const {
    t
  } = useTranslation('cropGuide');
  const actionRef = useRef<HTMLDivElement>(null);
  const {
    action,
    action_tag,
    action_type,
    description,
    files,
    id,
    initial_diagnosis,
    method,
    name,
    das,
    quantity,
    technical_content,
    unit
  } = cropAction;
  const hasProduct = technical_content != undefined && isArray(technical_content.products);
  const getHeading = () => {
    switch (action_type) {
      case CROP_ACTION.NUTRITION_MANAGEMENT:
        if (technical_content) {
          return `${t('add_nutrient_product')} - ${isArray(technical_content.products) ? technical_content.products[0].name : technical_content.name}`;
        }
        return `${t('add_nutrient_product')}`;
      case CROP_ACTION.CROP_PROBLEM:
        return `${t('problem_prevention')} - ${name}`;
      case CROP_ACTION.WATER_MANAGEMENT:
        return `${t('irrigation')} - ${method}`;
      default:
        return action;
    }
  };
  useEffect(() => {
    const fromApp = Boolean(getCookieValue(NAVIGATION_FROM_APP)) || Boolean(getCookieValue(NAVIGATION_FROM_APP_CODE));
    if (actionRef.current && isCurrent) {
      //distance between actionRef and its parent
      const offset = actionRef.current.offsetTop;
      window.scrollTo({
        //determines distance of current Action from the top of the page
        //150, 100 indicates value in px to reduce from the offset
        top: fromApp ? offset - 150 : offset - 100,
        behavior: 'smooth'
      });
    }
  }, [isCurrent]);
  return <div className={`flex pr-4 ${isCurrent ? 'bg-primary-10 border border-primary-70 rounded-2xl mx-2 pl-[15px]' : 'pl-6'}`} ref={actionRef} data-sentry-component="CropActionCard" data-sentry-source-file="CropActionCard.tsx">
      <div className="border-l w-0 border-dashed border-black">
        <div className={`border border-primary-100 rounded-full p-1 w-5 h-5 z-10 relative right-[10px] ${sowingDate ? 'top-12' : 'top-16'} ${isCurrent ? 'bg-primary-100' : 'bg-white'}`}>
          {isCurrent && <RightIcon className="fill-white stroke-white mt-0.5" height={8} width={10} />}
        </div>
      </div>

      <div className="ml-4 w-full pt-4">
        {sowingDate ? <Caption label={`${action_tag === ACTION_TAG.PAST ? t('completed_on') : t('complete_by')} ${getCropActionDate(das, sowingDate)}`} className="text-neutral-50 font-nato-semibold px-2" /> : <div className="flex items-center gap-8 px-2">
            <Caption label={t('know_task_date')} className="text-neutral-50 font-nato-semibold" />
            <button className="flex font-nato-semibold items-center text-sm text-primary-100 underline whitespace-nowrap" onClick={onClick}>
              {t('add_sowing_date')}
              <div className="bg-primary-100 inline-block ml-1.5 p-1 rounded-full">
                <CaretDownIcon className="fill-transparent h-3 -rotate-90 stroke-2 stroke-white w-3" />
              </div>
            </button>
          </div>}
        <SubHeading className="text-neutral-90 px-2 mt-2" data-sentry-element="SubHeading" data-sentry-source-file="CropActionCard.tsx">
          {getHeading()}
        </SubHeading>
        <Caption label={action_type === CROP_ACTION.CROP_PROBLEM ? initial_diagnosis as string : description as string} className="text-neutral-50 px-2 mt-1" data-sentry-element="Caption" data-sentry-source-file="CropActionCard.tsx" />
        {action_type === CROP_ACTION.CROP_PROBLEM ? <Link href={`/crop/${cropId}/problem/${id}`} className="flex font-nato-semibold items-center text-sm text-primary-100 underline whitespace-nowrap px-2 mt-2">
            {t('view_details')}
            <div className="bg-secondary-one-100 inline-block ml-1.5 p-1 rounded-full">
              <CaretDownIcon className="fill-transparent h-3 -rotate-90 stroke-2 stroke-white w-3" />
            </div>
          </Link> : null}
        <div className="pb-4">
          {action_type === CROP_ACTION.NUTRITION_MANAGEMENT && hasProduct ? <ProductCard content={technical_content as TechnicalContent} quantity={quantity as number} unit={unit as string} /> : null}
          {action_type !== CROP_ACTION.NUTRITION_MANAGEMENT && files && files.IMAGE ? <div className="relative mt-4 rounded-2xl bg-white h-44 max-w-sm">
              <Image src={files.IMAGE[0] || ''} fill alt={getHeading() as string} className="rounded-lg p-2" />
            </div> : null}
        </div>
      </div>
    </div>;
};
export default CropActionCard;